import type { GridLayoutItem } from "../sortable-grid";
import type { WidgetInstance } from "./types";

/**
 * The pure core of a widget board's undo/redo (`use-widget-board-history.ts`).
 * History steps are FULL board snapshots — placements AND membership — so
 * add/delete/duplicate/replace are undoable alongside drags, one coherent
 * system regardless of how the consumer persists the board. Config edits
 * deliberately never enter the stacks (they have their own editing surface
 * and debounce in bursts), which is why restoring a snapshot re-reads config
 * from the live board.
 */

/** True when `layout` places exactly the board's rows at the board's cells —
 *  membership AND rects. A gesture that lands back at its origin must not
 *  burn an undo step. */
export function samePlacements(
  board: WidgetInstance[],
  layout: GridLayoutItem[],
): boolean {
  if (board.length !== layout.length) return false;
  const byId = new Map(board.map((i) => [i.id, i]));
  return layout.every((l) => {
    const w = byId.get(l.id);
    return w && w.x === l.x && w.y === l.y && w.w === l.w && w.h === l.h;
  });
}

/** Apply a layout's rects onto a board. Board rows missing from the layout
 *  keep their cell; layout ids missing from the board are dropped (a widget
 *  deleted mid-gesture must not resurrect). */
export function applyPlacements(
  board: WidgetInstance[],
  layout: GridLayoutItem[],
): WidgetInstance[] {
  const cellById = new Map(layout.map((l) => [l.id, l]));
  return board.map((w) => {
    const cell = cellById.get(w.id);
    return cell && (cell.x !== w.x || cell.y !== w.y || cell.w !== w.w || cell.h !== w.h)
      ? { ...w, x: cell.x, y: cell.y, w: cell.w, h: cell.h }
      : w;
  });
}

/**
 * Rebuild a snapshot against the live board — the undo/redo restore step:
 *
 * - A snapshot row whose id survives with the SAME type keeps the live row's
 *   config at the snapshot's placement (config edits don't undo).
 * - A snapshot row whose id survives with a DIFFERENT type restores the
 *   snapshot row wholesale (undo of an in-place replace).
 * - A snapshot row gone from the live board re-inserts wholesale (undo of a
 *   delete).
 * - A live row absent from the snapshot drops (undo of an add).
 */
export function restoreBoardSnapshot(
  snapshot: WidgetInstance[],
  current: WidgetInstance[],
): WidgetInstance[] {
  const liveById = new Map(current.map((i) => [i.id, i]));
  return snapshot.map((snap) => {
    const live = liveById.get(snap.id);
    if (!live || live.type !== snap.type) return { ...snap };
    return { ...live, x: snap.x, y: snap.y, w: snap.w, h: snap.h };
  });
}
